import ArticleIcon from "@mui/icons-material/Article";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  Modal,
  ModalClose,
  ModalDialog,
  Sheet,
  Typography
} from "@mui/joy";
import DOMPurify from "dompurify";
import React, { useMemo, useState } from "react";
import {
  type ConfluencePageContent,
  type ConfluencePageInfo,
  type DocumentationType,
  useDocumentation,
  usePageContent
} from "../../api/documentation.hooks";

interface DocumentationDialogProps {
  open: boolean;
  onClose: () => void;
  docType: DocumentationType;
  /** Optional custom title for the dialog header */
  title?: string;
}

/** Helper to generate a human-readable title from docType */
const getDefaultTitle = (docType: DocumentationType): string => {
  const titles: Record<DocumentationType, string> = {
    workday: "Workday Integration Setup",
    google: "Google Domain Setup",
    office365: "Office 365 Domain Setup",
    okta: "Okta SSO Setup",
    azure: "Azure AD SSO Setup",
    ping: "PingIdentity SSO Setup",
    idaptive: "iDaptive SSO Setup"
  };
  return titles[docType] || "Documentation";
};

export const DocumentationDialog: React.FC<DocumentationDialogProps> = ({
  open,
  onClose,
  docType,
  title
}) => {
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);

  // Fetch documentation using React Query
  const {
    data: docData,
    isLoading,
    error: docError
  } = useDocumentation(docType, undefined, { enabled: open });

  // Fetch specific page content when a page is selected
  const {
    data: pageContent,
    isLoading: isLoadingPage,
    error: pageError
  } = usePageContent(selectedPageId, { enabled: !!selectedPageId && open });

  // Derive pages and current content from query data
  const pages: ConfluencePageInfo[] = docData?.pages || [];
  const currentContent: ConfluencePageContent | null =
    selectedPageId && pageContent ? pageContent : docData?.content || null;

  // Auto-select first page when documentation loads
  React.useEffect(() => {
    if (docData?.content?.pageId && !selectedPageId) {
      setSelectedPageId(docData.content.pageId);
    } else if (docData?.pages?.length && !selectedPageId && !docData?.content) {
      setSelectedPageId(docData.pages[0].pageId);
    }
  }, [docData, selectedPageId]);

  // Reset selected page when dialog closes
  React.useEffect(() => {
    if (!open) {
      setSelectedPageId(null);
    }
  }, [open]);

  const loadPageContent = (pageId: string) => {
    if (pageId !== selectedPageId) {
      setSelectedPageId(pageId);
    }
  };

  const handleClose = () => {
    setSelectedPageId(null);
    onClose();
  };

  // Combine errors
  const error =
    docError || pageError
      ? (docError as Error)?.message ||
        (pageError as Error)?.message ||
        "Failed to load documentation."
      : null;

  const dialogTitle = title || `${getDefaultTitle(docType)} Documentation`;

  // SECURITY: Sanitize HTML content to prevent XSS attacks (CWE-79)
  // Even though content comes from Confluence (trusted source), we sanitize
  // to protect against compromised Confluence accounts or misconfigured content
  //
  // PERFORMANCE: Images are modified to use lazy loading so text renders first
  // and users can start reading while images load in the background
  const sanitizedHtmlContent = useMemo(() => {
    if (!currentContent?.htmlContent) return "";

    const sanitized = DOMPurify.sanitize(currentContent.htmlContent, {
      // Allowlist of safe HTML tags for documentation content
      ALLOWED_TAGS: [
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "p",
        "a",
        "ul",
        "ol",
        "li",
        "code",
        "pre",
        "img",
        "strong",
        "em",
        "b",
        "i",
        "br",
        "hr",
        "div",
        "span",
        "table",
        "thead",
        "tbody",
        "tr",
        "th",
        "td",
        "blockquote"
      ],
      // Allowlist of safe attributes
      ALLOWED_ATTR: [
        "href",
        "src",
        "alt",
        "title",
        "class",
        "id",
        "target",
        "rel",
        "width",
        "height",
        "loading",
        "decoding"
      ],
      // Ensure links open safely
      ADD_ATTR: ["target", "rel"],
      // Force all links to have safe attributes
      FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
      // Block javascript: URLs
      ALLOW_DATA_ATTR: false
    });

    // Add lazy loading to all images for better performance
    // This ensures text loads first and users can start reading immediately
    return sanitized.replace(
      /<img\s/g,
      '<img loading="lazy" decoding="async" '
    );
  }, [currentContent?.htmlContent]);

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog
        sx={{
          width: "90vw",
          maxWidth: "1100px",
          height: "85vh",
          maxHeight: "800px",
          p: 0,
          overflow: "hidden"
        }}
      >
        <ModalClose />

        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            gap: 1
          }}
        >
          <MenuBookIcon color="primary" />
          <Typography level="title-lg">{dialogTitle}</Typography>
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            display: "flex",
            flex: 1,
            overflow: "hidden"
          }}
        >
          {/* Sidebar - only show if multiple pages */}
          {pages.length > 1 && (
            <>
              <Sheet
                variant="soft"
                sx={{
                  width: 260,
                  minWidth: 260,
                  borderRight: "1px solid",
                  borderColor: "divider",
                  overflowY: "auto"
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography level="body-sm" fontWeight="lg" sx={{ mb: 1 }}>
                    Documentation Pages
                  </Typography>
                  <List size="sm">
                    {pages.map((page) => (
                      <ListItem key={page.pageId}>
                        <ListItemButton
                          selected={selectedPageId === page.pageId}
                          onClick={() => loadPageContent(page.pageId)}
                          sx={{
                            borderRadius: "sm",
                            "&.Mui-selected": {
                              backgroundColor: "primary.softBg",
                              "&:hover": {
                                backgroundColor: "primary.softHoverBg"
                              }
                            }
                          }}
                        >
                          <ArticleIcon
                            sx={{
                              fontSize: 18,
                              mr: 1,
                              color:
                                selectedPageId === page.pageId
                                  ? "primary.main"
                                  : "neutral.500"
                            }}
                          />
                          <ListItemContent>
                            <Typography
                              level="body-sm"
                              sx={{
                                fontWeight:
                                  selectedPageId === page.pageId ? 600 : 400
                              }}
                            >
                              {page.title}
                            </Typography>
                          </ListItemContent>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Sheet>
              <Divider orientation="vertical" />
            </>
          )}

          {/* Content Area */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: 3
            }}
          >
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  gap: 2
                }}
              >
                <CircularProgress />
                <Typography level="body-md" color="neutral">
                  Loading documentation...
                </Typography>
              </Box>
            ) : error ? (
              <Alert color="danger" sx={{ m: 2 }}>
                <Typography level="body-sm">{error}</Typography>
              </Alert>
            ) : isLoadingPage ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  gap: 2
                }}
              >
                <CircularProgress size="sm" />
                <Typography level="body-sm" color="neutral">
                  Loading page...
                </Typography>
              </Box>
            ) : currentContent ? (
              <Box>
                {/* Page Title */}
                {pages.length === 1 && (
                  <Typography level="h3" sx={{ mb: 2 }}>
                    {currentContent.title}
                  </Typography>
                )}

                {/* Rendered HTML Content - Sanitized to prevent XSS */}
                {/* Confluence-style formatting for familiar documentation look */}
                {/* biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized with DOMPurify above */}
                <Box
                  dangerouslySetInnerHTML={{
                    __html: sanitizedHtmlContent
                  }}
                  sx={{
                    // Confluence-style typography
                    fontFamily:
                      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                    fontSize: "14px",
                    lineHeight: 1.714,
                    color: "#172B4D",

                    // Headings - Confluence style
                    "& h1": {
                      fontSize: "24px",
                      fontWeight: 500,
                      lineHeight: 1.167,
                      color: "#172B4D",
                      marginTop: "28px",
                      marginBottom: "12px",
                      paddingBottom: "8px",
                      borderBottom: "1px solid #DFE1E6",
                      "&:first-of-type": {
                        marginTop: 0
                      }
                    },
                    "& h2": {
                      fontSize: "20px",
                      fontWeight: 500,
                      lineHeight: 1.2,
                      color: "#172B4D",
                      marginTop: "24px",
                      marginBottom: "8px"
                    },
                    "& h3": {
                      fontSize: "16px",
                      fontWeight: 600,
                      lineHeight: 1.25,
                      color: "#172B4D",
                      marginTop: "20px",
                      marginBottom: "8px"
                    },
                    "& h4, & h5, & h6": {
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: 1.429,
                      color: "#172B4D",
                      marginTop: "16px",
                      marginBottom: "4px"
                    },

                    // Paragraphs
                    "& p": {
                      marginTop: "12px",
                      marginBottom: "12px",
                      "&:first-of-type": {
                        marginTop: 0
                      }
                    },

                    // Links - Confluence blue
                    "& a": {
                      color: "#0052CC",
                      textDecoration: "none",
                      "&:hover": {
                        color: "#0065FF",
                        textDecoration: "underline"
                      }
                    },

                    // Lists - Confluence style
                    "& ul, & ol": {
                      marginTop: "12px",
                      marginBottom: "12px",
                      paddingLeft: "24px"
                    },
                    "& li": {
                      marginTop: "4px",
                      marginBottom: "4px",
                      "& > ul, & > ol": {
                        marginTop: "4px",
                        marginBottom: "4px"
                      }
                    },
                    "& ul": {
                      listStyleType: "disc",
                      "& ul": {
                        listStyleType: "circle",
                        "& ul": {
                          listStyleType: "square"
                        }
                      }
                    },
                    "& ol": {
                      listStyleType: "decimal"
                    },

                    // Code - Confluence style
                    "& code": {
                      backgroundColor: "#F4F5F7",
                      borderRadius: "3px",
                      padding: "2px 4px",
                      fontFamily:
                        '"SFMono-Medium", "SF Mono", "Segoe UI Mono", "Roboto Mono", "Ubuntu Mono", Menlo, Consolas, Courier, monospace',
                      fontSize: "12px",
                      color: "#172B4D"
                    },
                    "& pre": {
                      backgroundColor: "#F4F5F7",
                      borderRadius: "3px",
                      padding: "12px",
                      overflow: "auto",
                      marginTop: "12px",
                      marginBottom: "12px",
                      "& code": {
                        backgroundColor: "transparent",
                        padding: 0,
                        display: "block",
                        whiteSpace: "pre"
                      }
                    },

                    // Tables - Confluence style
                    "& table": {
                      borderCollapse: "collapse",
                      width: "100%",
                      marginTop: "16px",
                      marginBottom: "16px"
                    },
                    "& th": {
                      backgroundColor: "#F4F5F7",
                      borderBottom: "2px solid #DFE1E6",
                      padding: "8px 12px",
                      textAlign: "left",
                      fontWeight: 600,
                      color: "#172B4D",
                      fontSize: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em"
                    },
                    "& td": {
                      borderBottom: "1px solid #DFE1E6",
                      padding: "8px 12px",
                      verticalAlign: "top"
                    },
                    "& tr:hover td": {
                      backgroundColor: "#FAFBFC"
                    },

                    // Blockquotes - Confluence info panel style
                    "& blockquote": {
                      backgroundColor: "#DEEBFF",
                      borderLeft: "4px solid #0052CC",
                      borderRadius: "3px",
                      margin: "16px 0",
                      padding: "12px 16px",
                      color: "#172B4D",
                      "& p": {
                        margin: 0
                      }
                    },

                    // Images - responsive with lazy loading placeholder
                    "& img": {
                      maxWidth: "100%",
                      height: "auto",
                      borderRadius: "3px",
                      marginTop: "12px",
                      marginBottom: "12px",
                      backgroundColor: "#F4F5F7",
                      // Placeholder for lazy-loaded images
                      minHeight: "100px",
                      transition: "opacity 0.3s ease-in-out"
                    },

                    // Horizontal rules
                    "& hr": {
                      border: "none",
                      borderTop: "1px solid #DFE1E6",
                      margin: "24px 0"
                    },

                    // Strong and emphasis
                    "& strong, & b": {
                      fontWeight: 600
                    },
                    "& em, & i": {
                      fontStyle: "italic"
                    },

                    // Div containers (often used for panels in Confluence)
                    "& div": {
                      // Info panel style
                      "&[class*='info'], &[class*='note']": {
                        backgroundColor: "#DEEBFF",
                        borderRadius: "3px",
                        padding: "12px 16px",
                        margin: "16px 0",
                        borderLeft: "4px solid #0052CC"
                      },
                      // Warning panel style
                      "&[class*='warning']": {
                        backgroundColor: "#FFFAE6",
                        borderRadius: "3px",
                        padding: "12px 16px",
                        margin: "16px 0",
                        borderLeft: "4px solid #FFAB00"
                      },
                      // Error/danger panel style
                      "&[class*='error'], &[class*='danger']": {
                        backgroundColor: "#FFEBE6",
                        borderRadius: "3px",
                        padding: "12px 16px",
                        margin: "16px 0",
                        borderLeft: "4px solid #DE350B"
                      },
                      // Success panel style
                      "&[class*='success'], &[class*='tip']": {
                        backgroundColor: "#E3FCEF",
                        borderRadius: "3px",
                        padding: "12px 16px",
                        margin: "16px 0",
                        borderLeft: "4px solid #00875A"
                      }
                    }
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "neutral.500"
                }}
              >
                <MenuBookIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography level="body-md">
                  Select a documentation page to view
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </ModalDialog>
    </Modal>
  );
};

export default DocumentationDialog;
